import React, { useEffect, useRef, useState } from "react";
import { QRCode } from "react-qrcode-logo";
import { asset, assetCategory } from "../../../api/index.js";
import { toast } from "react-toastify";
import { LocationFilter } from "../../../components/LocationFilter/index.jsx";
import SearchableFilter from "../../../components/SearchableFilter";
import DebounceInput from "../../../components/form/HorizontalDebounceInput";
import { useAuth } from "../../../contexts/AuthContext.jsx";

// A4 sticker layout constants
// A4 at 96dpi = 794px wide × 1123px tall (approx)
// We use fixed sticker heights to compute rows-per-A4-page
const STICKER_HEIGHT_PX = 110; // approximate rendered height per sticker card
const A4_USABLE_HEIGHT_PX = 1060; // A4 height minus margins
const ROWS_PER_A4 = Math.floor(A4_USABLE_HEIGHT_PX / STICKER_HEIGHT_PX); // ~9

function calcPageSize(cols) {
  return cols * ROWS_PER_A4;
}

function AssetQRTab({ categories }) {
  const { config } = useAuth();
  const [datas, setDatas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cols, setCols] = useState(2); // 2 or 3
  const [query, setQuery] = useState({
    page: 1,
    take: calcPageSize(3),
    totalData: 0,
    search: "",
    location_id: "",
    category_id: "",
  });
  const printRef = useRef(null);

  const totalPages = Math.max(1, Math.ceil(query.totalData / query.take));

  async function getData(q) {
    try {
      setLoading(true);
      const res = await asset.list(q);
      if (res.ok) {
        const body = await res.json();
        const data = body.data;
        if (data?.results) {
          setDatas(data.results);
        }
        if (data?.totalData !== undefined) {
          setQuery((prev) => ({ ...prev, totalData: data.totalData }));
        }
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error(e.message);
    }
  }

  useEffect(() => {
    getData(query);
    // eslint-disable-next-line
  }, [query.page, query.take, query.location_id, query.category_id, query.search]);

  function handleColChange(newCols) {
    const newTake = calcPageSize(newCols);
    setCols(newCols);
    setQuery((prev) => ({ ...prev, page: 1, take: newTake }));
  }

  function handlePrint() {
    window.print();
  }

  const handleChangeLocationFilter = (location) => {
    setQuery((prev) => ({
      ...prev,
      page: 1,
      location_id: location ? location.id : "",
    }));
  };

  const handleChangeCategory = (id) => {
    setQuery((prev) => ({
      ...prev,
      page: 1,
      category_id: id || "",
    }));
  };

  const handleChangeKeyword = (e) => {
    setQuery((prev) => ({ ...prev, page: 1, search: e.target.value }));
  };

  const companyLogo = "/cmms/logo.png";

  return (
    <div className="asset-qr-tab">
      {/* ── Toolbar (hidden on print) ── */}
      <div className="qr-toolbar no-print">
        {/* Filters row */}
        <div className="row mb-2 g-2 align-items-center">
          <div className="col-sm-3">
            <DebounceInput
              name="keyword"
              mb0={true}
              placeholder="Search Asset name"
              showDebounceIndicator
              delay={1000}
              onchange={handleChangeKeyword}
            />
          </div>
          <div className="col-sm-3">
            <LocationFilter onChange={handleChangeLocationFilter} />
          </div>
          <div className="col-sm-3" style={{ zIndex: 11, position: "relative" }}>
            <SearchableFilter
              label="Category"
              onChange={(e) => handleChangeCategory(e?.value)}
              list={(categories || []).map((i) => ({ value: i.id, label: i.name }))}
            />
          </div>
          <div className="col-sm-3 d-flex gap-2 justify-content-end flex-wrap">
            {/* Column toggle */}
            <div className="btn-group" role="group" aria-label="Column layout">
              <button
                type="button"
                className={`btn ${cols === 2 ? "btn-theme" : "btn-outline-theme"}`}
                onClick={() => handleColChange(2)}
                title="2 Columns"
              >
                <i className="bi bi-grid me-1" />2 Col
              </button>
              <button
                type="button"
                className={`btn ${cols === 3 ? "btn-theme" : "btn-outline-theme"}`}
                onClick={() => handleColChange(3)}
                title="3 Columns"
              >
                <i className="bi bi-grid-3x3-gap me-1" />3 Col
              </button>
            </div>
            {/* Print button */}
            <button
              type="button"
              className="btn btn-outline-success"
              onClick={handlePrint}
              title="Print sticker sheet"
            >
              <i className="bi bi-printer me-1" />
              Print
            </button>
          </div>
        </div>

        {/* Pagination info + navigation */}
        <div className="d-flex align-items-center gap-3 mb-3">
          <small className="text-muted">
            {loading ? (
              "Loading…"
            ) : (
              <>
                Page <strong>{query.page}</strong> of <strong>{totalPages}</strong>{" "}
                &nbsp;·&nbsp; {query.totalData} asset(s) &nbsp;·&nbsp;{" "}
                {query.take} per sheet
              </>
            )}
          </small>
          <div className="btn-group btn-group-sm">
            <button
              className="btn btn-outline-secondary"
              disabled={query.page <= 1 || loading}
              onClick={() => setQuery((p) => ({ ...p, page: p.page - 1 }))}
            >
              <i className="bi bi-chevron-left" />
            </button>
            <button
              className="btn btn-outline-secondary"
              disabled={query.page >= totalPages || loading}
              onClick={() => setQuery((p) => ({ ...p, page: p.page + 1 }))}
            >
              <i className="bi bi-chevron-right" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Print Area ── */}
      <div className="qr-print-area" ref={printRef}>
        {/* A4 Paper sheet */}
        <div className="qr-paper">
          {loading ? (
            <div className="qr-loading-overlay no-print">
              <div className="spinner-border text-secondary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
              <span className="ms-2 text-muted">Loading assets…</span>
            </div>
          ) : datas.length === 0 ? (
            <div className="qr-empty no-print">
              <i className="bi bi-qr-code" style={{ fontSize: "3rem", opacity: 0.3 }} />
              <p className="mt-2 text-muted">No assets found</p>
            </div>
          ) : (
            <div
              className="qr-grid"
              style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
            >
              {datas.map((item) => (
                <StickerCard key={item.id} item={item} logoImage={companyLogo} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Inline styles (including @media print) ── */}
      <style>{`
        /* === Screen layout === */
        .asset-qr-tab {
          font-family: 'Inter', 'Segoe UI', sans-serif;
        }

        .qr-print-area {
          display: flex;
          justify-content: center;
          padding: 16px 0 32px;
        }

        .qr-paper {
          background: #ffffff;
          color: #111;
          width: 794px;           /* A4 at 96dpi */
          min-height: 1123px;     /* A4 at 96dpi */
          padding: 28px 24px;
          border-radius: 4px;
          box-shadow: 0 4px 32px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06);
          position: relative;
        }

        .qr-grid {
          display: grid;
          gap: 8px;
        }

        /* === Sticker card === */
        .sticker-card {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          border: 1px solid #ccc;
          border-radius: 4px;
          padding: 8px 10px;
          min-height: 100px;
          background: #fff;
          page-break-inside: avoid;
          break-inside: avoid;
        }

        .sticker-qr {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 80px;
          height: 80px;
        }

        .sticker-divider {
          width: 1px;
          background: #ddd;
          height: 70%;
          margin: 0 10px;
          flex-shrink: 0;
        }

        .sticker-info {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 2px;
        }

        .sticker-customer {
          font-size: 14px;
          font-weight: 700;
          color: #333;
          letter-spacing: 0.3px;
          text-transform: uppercase;
          word-break: break-word;
        }

        .sticker-asset-name {
          font-size: 12px;
          font-weight: 400;
          color: #111;
          line-height: 1.3;
          word-break: break-word;
        }

        .sticker-barcode {
          font-size: 9px;
          color: #888;
          font-family: 'Courier New', monospace;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin-top: 2px;
        }

        .qr-loading-overlay,
        .qr-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          color: #aaa;
        }

        /* === Print styles === */
        @media print {
          /* Hide all page chrome */
          body * {
            visibility: hidden;
          }

          /* Show only the print area */
          .qr-print-area,
          .qr-print-area * {
            visibility: visible;
          }

          .qr-print-area {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            padding: 0;
            margin: 0;
          }

          .qr-paper {
            width: 100%;
            min-height: auto;
            box-shadow: none;
            border-radius: 0;
            padding: 10mm 12mm;
          }

          .qr-grid {
            gap: 4px;
          }

          .sticker-card {
            border: 1px solid #bbb;
            height: 90px;
            page-break-inside: avoid;
            break-inside: avoid;
          }

          .no-print {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}

/**
 * Traverse nested location tree to find the root ancestor.
 * The root is the node with no parent_location — used as the customer/company name.
 */
function getRootLocationName(location) {
  if (!location) return "";
  let node = location;
  while (node.parent_location) {
    node = node.parent_location;
  }
  return node.location_name || "";
}

/**
 * Individual sticker card
 */
function StickerCard({ item, logoImage }) {
  const customerName =
    getRootLocationName(item.location) ||
    item.customer?.customer_name ||
    item.customer?.name ||
    "";

  const assetName = item.asset_name || "-";
  const barcode = item.barcode || item.id;

  return (
    <div className="sticker-card">
      {/* QR Code */}
      <div className="sticker-qr">
        <QRCode
          value={String(barcode)}
          logoImage={logoImage}
          size={76}
          quietZone={2}
          logoWidth={18}
          logoHeight={18}
          removeQrCodeBehindLogo
          qrStyle="dots"
          eyeRadius={3}
        />
      </div>

      {/* Vertical divider */}
      <div className="sticker-divider" />

      {/* Text info */}
      <div className="sticker-info">
        {customerName && (
          <div className="sticker-customer">{customerName}</div>
        )}
        <div className="sticker-asset-name">{assetName}</div>
      </div>
    </div>
  );
}

export default AssetQRTab;
